import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { IViz, VizType } from '../../../ts/models/Viz';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export interface IWorkbookRevisionVizViewerScope extends ng.IScope {
  viz: IViz;
  headerTitleText: string;
  closePreview: () => void;
}

export interface RevisionHistoryInfo {
  readonly workbookUrlId: string;
  readonly revisionNumber: string;  // should be number
  readonly sheetUrlId: string;
}

export interface RevisionHistorySpaceProviderOwnProps extends ReactDirectiveProps {
  readonly revisionHistoryInfo: RevisionHistoryInfo;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope) => JSX.Element;
}

export interface RevisionHistorySpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
}

export interface RevisionHistorySpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
}

const template = `<div ng-include="'workbookRevisionVizViewer.html'"></div>`;

export const renderRevisionHistorySpace = (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={angularContext}
        setupScope={setupScope}
      />
    </div>
  );
};

export type RevisionHistorySpaceProviderPropsWithoutAngular = RevisionHistorySpaceProviderOwnProps & RevisionHistorySpaceProviderStateProps & RevisionHistorySpaceProviderDispatchProps;
export type RevisionHistorySpaceProviderProps = RevisionHistorySpaceProviderPropsWithoutAngular;

export class RevisionHistorySpaceProvider extends React.Component<RevisionHistorySpaceProviderProps> {

  private spaceScope: IWorkbookRevisionVizViewerScope = null;


  public constructor(props: RevisionHistorySpaceProviderProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public render() {
    return this.props.renderSpace(this.props.angularContext, this.setupScope);
  }

  public componentDidMount() {
    // initial viz path for analysis vizzes
    this.props.setVizPath(getRevisionHistoryVizPath(this.props.revisionHistoryInfo));
    BrowserTitleService.setTitleToServerName();
  }

  public componentDidUpdate(prevProps: RevisionHistorySpaceProviderProps) {
    const prevVizPathFromUrl = getRevisionHistoryVizPath(prevProps.revisionHistoryInfo);
    const currVizPathFromUrl = getRevisionHistoryVizPath(this.props.revisionHistoryInfo);
    const vizPathFromAppState = this.props.vizPath;

    if (prevVizPathFromUrl !== currVizPathFromUrl && currVizPathFromUrl !== vizPathFromAppState) {
      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      this.props.setVizPath(currVizPathFromUrl);

      if (this.spaceScope) {
        const previousViz: IViz = this.spaceScope.viz;

        this.spaceScope.headerTitleText = Messages.revisionHistoryPreviewTitle({ REVISION_NUMBER: this.props.revisionHistoryInfo.revisionNumber });

        this.spaceScope.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.RevisionPreview,
          params: queryString.parse(this.props.routeLocationSearch || ''),
          allowReloadViz: true,  // this needs to be true so we can reload the viz when the url changes

          reloadViz: previousViz.reloadViz,
          isExplicitOriginalViz: previousViz.isExplicitOriginalViz,
          instanceId: previousViz.instanceId,
        };
        this.spaceScope.$apply();
        this.spaceScope.viz.reloadViz && this.spaceScope.viz.reloadViz();
      }
    }
  }

  public shouldComponentUpdate(nextProps: RevisionHistorySpaceProviderProps) {
    const nextVizPathFromUrl = getRevisionHistoryVizPath(nextProps.revisionHistoryInfo);
    if (nextProps.vizPath !== VizPathInitialState &&
      nextProps.vizPath !== this.props.vizPath &&
      nextProps.vizPath !== nextVizPathFromUrl) {
      // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
      // so don't rerender
      return false;
    }

    return true;
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IWorkbookRevisionVizViewerScope;

    childScope.headerTitleText = Messages.revisionHistoryPreviewTitle({ REVISION_NUMBER: this.props.revisionHistoryInfo.revisionNumber });
    const path = getRevisionHistoryVizPath(this.props.revisionHistoryInfo);

    childScope.viz = {
      path: path,
      vizType: VizType.RevisionPreview,
      siteUrlName: this.props.sessionSiteUrlName,
      params: queryString.parse(this.props.routeLocationSearch || ''),
      allowReloadViz: true  // this needs to be true so we can reload the viz when the url changes
    };

    childScope.closePreview = () => { window.close(); };

    this.spaceScope = childScope;
    return childScope;
  }
}

export function getRevisionHistoryVizPath(revisionHistoryInfo: RevisionHistoryInfo) {
  // use compact because sheetUrlId can be undefined
  const pathElements = _.compact([
    revisionHistoryInfo.workbookUrlId,
    revisionHistoryInfo.sheetUrlId,
    'history',
    revisionHistoryInfo.revisionNumber
  ]);
  const path = pathElements.join('/');
  return path;
}

const mapStateToProps: MapStateToProps<RevisionHistorySpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState)
});

const mapDispatchToProps: MapDispatchToProps<RevisionHistorySpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath
}, dispatch);

export const ConnectedRevisionHistorySpaceProvider: React.ComponentClass<RevisionHistorySpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(RevisionHistorySpaceProvider);
